import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SLUG_PATTERN, VILLAGE_SLUG_MAX_LENGTH, normSlug, slugBlur } from '@/lib/slug';
import { api, type AdminVillage } from '@/lib/api';
import { TranslationsField } from '@/components/TranslationsField';
import { LocationPickerMap } from '@/components/LocationPickerMap';

export const Route = createFileRoute('/_authed/villages/')({
  component: VillagesPage,
});

const tr = z.object({
  en: z.string().min(1),
  el: z.string().optional(),
  es: z.string().optional(),
  de: z.string().optional(),
  it: z.string().optional(),
  ru: z.string().optional(),
  he: z.string().optional(),
  pl: z.string().optional(),
  uk: z.string().optional(),
  fr: z.string().optional(),
  ar: z.string().optional(),
});

const createSchema = z.object({
  slug: z.preprocess(
    normSlug(VILLAGE_SLUG_MAX_LENGTH),
    z
      .string()
      .min(1, 'Needs at least one letter or digit')
      .max(VILLAGE_SLUG_MAX_LENGTH, `At most ${VILLAGE_SLUG_MAX_LENGTH} characters`)
      .regex(SLUG_PATTERN, 'Lowercase letters, digits and single hyphens'),
  ),
  name: tr,
  description: tr,
  lat: z.coerce.number(),
  lng: z.coerce.number(),
});
type CreateValues = z.infer<typeof createSchema>;

function VillagesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['villages'],
    queryFn: api.listVillages,
  });

  const create = useMutation({
    mutationFn: (v: CreateValues) => api.createVillage(v as Partial<AdminVillage>),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['villages'] });
      setCreating(false);
      // Jump straight into the new village so the user can add a route +
      // POIs without an extra click.
      navigate({
        to: '/villages/$villageId',
        params: { villageId: data.village.id },
      });
    },
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-h1">Villages</h1>
          <p className="mt-1 text-body text-slate-gray">
            Manage the Cypriot villages that make up the Explorentia platform.
          </p>
        </div>
        {!creating ? (
          <button className="btn-primary" onClick={() => setCreating(true)}>
            + New village
          </button>
        ) : null}
      </div>

      {creating ? (
        <CreateVillageForm
          onSubmit={(v) => create.mutateAsync(v)}
          submitting={create.isPending}
          error={create.error ? String(create.error) : null}
          onCancel={() => setCreating(false)}
        />
      ) : null}

      {isLoading ? (
        <div className="card text-slate-gray">Loading…</div>
      ) : error ? (
        <div className="card text-error">Failed to load villages: {String(error)}</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data?.villages.map((v) => (
            <Link
              key={v.id}
              to="/villages/$villageId"
              params={{ villageId: v.id }}
              className="group rounded-card border border-neutral-gray/50 bg-soft-white p-5 shadow-card transition hover:border-tech-blue/40"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-h2 group-hover:text-tech-blue">{v.name.en}</h2>
                <span className="rounded-full bg-light-gray px-2 py-0.5 font-mono text-caption text-slate-gray">
                  {v.slug}
                </span>
              </div>
              <p className="mt-2 line-clamp-3 text-body text-slate-gray">
                {v.description.en}
              </p>
              <p className="mt-3 text-caption text-slate-gray">
                {v.lat.toFixed(4)}, {v.lng.toFixed(4)}
              </p>
            </Link>
          ))}
          {data && data.villages.length === 0 ? (
            <div className="card col-span-full text-slate-gray">No villages yet.</div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function CreateVillageForm({
  onSubmit,
  submitting,
  error,
  onCancel,
}: {
  onSubmit: (v: CreateValues) => Promise<unknown>;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      slug: '',
      name: { en: '' },
      description: { en: '' },
      lat: 0,
      lng: 0,
    },
  });
  const lat = watch('lat');
  const lng = watch('lng');

  return (
    <form
      onSubmit={handleSubmit((v) => onSubmit(v))}
      className="card mb-6 space-y-4"
    >
      <h2 className="text-h2">New village</h2>
      <div>
        <label className="label">Slug</label>
        <input
          className="input font-mono"
          placeholder="omodos"
          {...register('slug', slugBlur(setValue, VILLAGE_SLUG_MAX_LENGTH))}
        />
        {errors.slug ? (
          <p className="mt-1 text-caption text-error">{errors.slug.message}</p>
        ) : null}
      </div>

      <TranslationsField
        label="Name"
        required
        basePath="name"
        register={register}
        setValue={setValue}
        watch={watch}
      />
      <TranslationsField
        label="Description"
        required
        multiline
        basePath="description"
        register={register}
        setValue={setValue}
        watch={watch}
      />

      <LocationPickerMap
        label="Village centre"
        value={{ lat: Number(lat) || 0, lng: Number(lng) || 0 }}
        onChange={(next) => {
          setValue('lat', next.lat, { shouldDirty: true });
          setValue('lng', next.lng, { shouldDirty: true });
        }}
      />

      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-caption text-error">
          {error}
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create village'}
        </button>
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
